import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { ThemeContextType } from '../types/theme';

/**
 * Custom hook to access theme context
 * This hook provides access to the current theme, mode, and setMode function
 *
 * @returns {ThemeContextType} Theme context with theme object, mode, and setMode function
 *
 * @example
 * ```tsx
 * import { useTheme } from '../hooks/useTheme';
 *
 * function MyComponent() {
 *   const { theme, mode, setMode } = useTheme();
 *
 *   return (
 *     <div style={{
 *       backgroundColor: theme.colors.background,
 *       color: theme.colors.text.primary
 *     }}>
 *       Current mode: {mode}
 *     </div>
 *   );
 * }
 * ```
 */
export const useTheme = (): ThemeContextType => {
  return useThemeContext();
};

/**
 * Hook to get only the theme object
 * Useful when you only need the theme styles without mode management
 *
 * @returns {Theme} Current theme object
 *
 * @example
 * ```tsx
 * import { useThemeStyles } from '../hooks/useTheme';
 *
 * function MyComponent() {
 *   const theme = useThemeStyles();
 *
 *   return (
 *     <div style={{
 *       backgroundColor: theme.colors.background,
 *       color: theme.colors.text.primary
 *     }}>
 *       Content
 *     </div>
 *   );
 * }
 * ```
 */
export const useThemeStyles = () => {
  const { theme } = useThemeContext();
  return theme;
};

/**
 * Hook to get only the current theme mode
 * Useful when you only need to check the current mode
 *
 * @returns {ThemeMode} Current theme mode ('light' or 'dark')
 *
 * @example
 * ```tsx
 * import { useThemeMode } from '../hooks/useTheme';
 *
 * function MyComponent() {
 *   const mode = useThemeMode();
 *
 *   return (
 *     <div>
 *       Current theme: {mode}
 *     </div>
 *   );
 * }
 * ```
 */
export const useThemeMode = () => {
  const { mode } = useThemeContext();
  return mode;
};
